import React from 'react';
import {EmailLabelWithCopy, ErrorDisplay, LoadingSpinner, PORTAL_NAVY} from '../../../common';
import {formatISODateStr} from '../../../common/util/date';
import {fullName} from './BusinessContactTable';
import {BusinessContactHistoryEntry, BusinessContactRow} from './domain/types';
import {formatUsPhone} from './domain/phone';
import {historySentences, STATUS_SENTENCE_LEAD} from './historySentences';
import {useContactHistory} from './useContactHistory';

type Props = {
    /** The row the clock was clicked on. The header names it and the query reads its guid. */
    contact: BusinessContactRow;
    onClose: () => void;
};

/**
 * The grid's Updated column format, so one change reads the same in the grid and the panel.
 * No zone label, a decision on the ticket.
 */
const DATE_FORMAT = 'MM/dd/yyyy hh:mm a';
/** The form's Cancel button colour, so the two ways out of the drawer look the same. */
const CLOSE_BLUE = '#1A6DFF';

const isBlank = (value: string | null | undefined): boolean => (value ?? '').trim() === '';

/**
 * Who made the change. A Portal colleague is a mailto label, the roster's Uploaded By shape, so the
 * reader can write to them. A Salesforce user is plain text marked "via Salesforce" so ops can tell
 * the two writers apart. Salesforce sometimes withholds the author; then the line reads "by
 * Salesforce" rather than showing a hole.
 */
const Actor = ({entry}: {entry: BusinessContactHistoryEntry}) => {
    const name = (entry.actorName ?? '').trim();
    if (entry.source === 'SALESFORCE') {
        return <>{name === '' ? 'Salesforce' : `${name} via Salesforce`}</>;
    }
    if (name === '') {
        return <>Portal</>;
    }
    const email = (entry.actorEmail ?? '').trim();
    return email === '' ? <>{name}</> : <EmailLabelWithCopy email={email} label={name}/>;
};

/**
 * One sentence of an entry. The status line ends in the value the contact moved to, and the mockup
 * sets that value in bold so status changes stand out from the field edits around them.
 */
const Sentence = ({text}: {text: string}) => {
    if (!text.startsWith(STATUS_SENTENCE_LEAD)) {
        return <>{text}</>;
    }
    return <>{STATUS_SENTENCE_LEAD}<span className="font-semibold">{text.slice(STATUS_SENTENCE_LEAD.length)}</span></>;
};

/**
 * One line of the timeline: a dot on the rail, then the date, the sentences and the actor. The rail
 * and the rule both join an entry to the one below it, so the oldest entry carries neither: a line
 * running on past the last dot reads as an entry the panel failed to draw.
 */
const HistoryEntry = ({entry, last}: {entry: BusinessContactHistoryEntry; last: boolean}) => (
    <li className="flex">
        <div className="flex flex-col items-center mr-5" aria-hidden="true">
            <span className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full border border-gray-400 bg-white"/>
            {!last && <span className="flex-grow mt-1 border-l border-gray-300"/>}
        </div>
        <div className={`flex-grow text-sm ${last ? '' : 'pb-4 mb-5 border-b border-gray-200'}`}>
            <p className="text-gray-700">{formatISODateStr(entry.changedAt, DATE_FORMAT)}</p>
            {historySentences(entry).map((sentence) =>
                <p key={sentence} className="mt-1"><Sentence text={sentence}/></p>)}
            <p className="mt-1">by <Actor entry={entry}/></p>
        </div>
    </li>
);

/**
 * The Contact History side panel (BHPNPP-3248). It rides inside ContactDrawer under the "Contact
 * History" title: the contact's name, title and phone as a header, then the timeline newest first,
 * then Close. The backend orders the entries and builds the created line, so the panel only reads.
 */
export const ContactHistoryPanel = ({contact, onClose}: Props) => {
    const {entries, loading, error} = useContactHistory(contact.guid);

    return (
        <div className="flex flex-col px-6 pb-6">
            {/* The title and the phone read at the timeline's size, with the leading opened up and
                the rule well clear of the first entry, the way the mockup spaces them. */}
            <div className="pb-4 mb-10 border-b border-gray-200">
                <p className="text-xl font-bold" style={{color: PORTAL_NAVY}}>{fullName(contact)}</p>
                {!isBlank(contact.title) &&
                    <p className="mt-1 text-sm leading-relaxed text-gray-700">{contact.title}</p>}
                {!isBlank(contact.contactPhone) &&
                    <p className="text-sm leading-relaxed text-gray-700">{formatUsPhone(contact.contactPhone as string)}</p>}
            </div>
            {loading && <LoadingSpinner spacing={20}/>}
            {!loading && error && <ErrorDisplay error={error} title="Error loading contact history"/>}
            {!loading && !error && entries.length === 0 && <p className="text-sm text-gray-700">No history yet</p>}
            {!loading && !error && entries.length > 0 && (
                <ol className="list-none">
                    {entries.map((entry, index) =>
                        // The created line the backend builds has no guid of its own.
                        <HistoryEntry key={entry.guid ?? `created-${index}`}
                                      entry={entry}
                                      last={index === entries.length - 1}/>)}
                </ol>
            )}
            {/* Sized to its label rather than the drawer, smaller than the form's Cancel: the form
                asks for a decision, this panel is only read. */}
            <div className="mt-10">
                <button type="button"
                        onClick={onClose}
                        className="h-8 px-4 rounded text-sm font-semibold bg-white border focus:outline-none"
                        style={{color: CLOSE_BLUE, borderColor: CLOSE_BLUE}}>
                    Close
                </button>
            </div>
        </div>
    );
};
